/**
 * MapLens — theme-territory map for the Histories Braid+Map redesign.
 * Every moment is a point on a 2D map (theme centroid coordinates from the
 * backend clustering); real themes get a covariance-ellipse "territory";
 * clicking a territory label focuses it and reveals its early->recent drift
 * arrow. A dual-handle time window ghosts/hides moments outside the range,
 * with a play button that sweeps the window forward. Controlled by
 * StoryPanel via selectedMomentId; emits onSelectMoment.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EGYEB_TONE, VALENCE_TOKENS } from "./storyPalette";
import { useStoryTooltip } from "./StoryTooltip";
import { formatShortDate } from "./braidGeometry";
import type { StoryMapOk, StoryMoment, StoryTheme } from "./types";
import {
  MAP_VIEW_W,
  MAP_VIEW_H,
  centroid,
  covEllipse,
  driftArrow,
  inkAge,
  projectMoment,
  splitChronological,
  territoryOpacity,
  type Ellipse,
} from "./mapGeometry";
import "./MapLens.css";

export interface MapLensProps {
  data: StoryMapOk;
  selectedMomentId: number | null;
  onSelectMoment: (momentId: number) => void;
}

interface TerritoryEntry {
  theme: StoryTheme;
  moments: StoryMoment[];
  e1: Ellipse;
  e16: Ellipse;
}

const HALO_EXTRA = 4.5;
const SELECTION_EXTRA = 4;
const PLAY_INTERVAL_MS = 900;

export function MapLens({ data, selectedMomentId, onSelectMoment }: MapLensProps) {
  const { t, i18n } = useTranslation();
  const { tooltip, showTip, moveTip, hideTip } = useStoryTooltip();
  const uid = useId();
  const gratId = `map-grat-${uid}`;
  const vignId = `map-vign-${uid}`;

  const sessions = data.sessions;
  const lastIndex = sessions.length - 1;
  if (lastIndex <= 0) {
    throw new Error(
      `[MapLens] story map needs at least two sessions, got ${sessions.length}`,
    );
  }

  const [windowA, setWindowA] = useState(0);
  const [windowB, setWindowB] = useState(lastIndex);
  const [focusedThemeId, setFocusedThemeId] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (playRef.current) {
        clearInterval(playRef.current);
        playRef.current = null;
      }
    };
  }, []);

  const sessionIndex = useMemo(
    () => new Map(sessions.map((s, i) => [s.id, i] as const)),
    [sessions],
  );
  const themeMap = useMemo(
    () => new Map(data.themes.map((th) => [th.id, th] as const)),
    [data.themes],
  );
  const projected = useMemo(() => {
    const map = new Map<number, [number, number]>();
    for (const m of data.moments) map.set(m.id, projectMoment(m.x, m.y));
    return map;
  }, [data.moments]);

  const dateIndexOf = (m: StoryMoment): number => {
    const idx = sessionIndex.get(m.session_id);
    if (idx == null) {
      throw new Error(`[MapLens] unknown session id: ${m.session_id}`);
    }
    return idx;
  };
  const themeOf = (m: StoryMoment): StoryTheme => {
    const th = themeMap.get(m.theme_id);
    if (!th) {
      throw new Error(`[MapLens] unknown theme id: ${m.theme_id}`);
    }
    return th;
  };
  const themeLabel = (th: StoryTheme): string =>
    th.id === -1 ? t("stories.egyeb") : th.label ?? "";
  const themeTone = (th: StoryTheme): string =>
    th.id === -1 ? EGYEB_TONE : VALENCE_TOKENS[th.valence].tone;

  const territoryThemes = useMemo(() => {
    const list: TerritoryEntry[] = [];
    for (const th of data.themes) {
      if (th.id < 0) continue;
      const moments = data.moments.filter((m) => m.theme_id === th.id);
      const pts = moments.map((m) => projected.get(m.id)!);
      const e1 = covEllipse(pts, 1);
      const e16 = covEllipse(pts, 1.6);
      if (e1 && e16) list.push({ theme: th, moments, e1, e16 });
    }
    return list;
  }, [data.themes, data.moments, projected]);

  const visibleFraction = (moments: StoryMoment[]): number => {
    if (moments.length === 0) return 0;
    let vis = 0;
    for (const m of moments) {
      const di = dateIndexOf(m);
      if (di >= windowA && di <= windowB) vis++;
    }
    return vis / moments.length;
  };

  const focusedEntry =
    focusedThemeId != null
      ? territoryThemes.find((te) => te.theme.id === focusedThemeId) ?? null
      : null;

  const drift = useMemo(() => {
    if (!focusedEntry) return null;
    const sorted = focusedEntry.moments
      .slice()
      .sort((a, b) => dateIndexOf(a) - dateIndexOf(b));
    const [early, recent] = splitChronological(sorted);
    const earlyPts = early.map((m) => projected.get(m.id)!);
    const recentPts = recent.map((m) => projected.get(m.id)!);
    return driftArrow(centroid(earlyPts), centroid(recentPts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedEntry, projected]);

  const toggleFocus = (themeId: number) => {
    setFocusedThemeId((cur) => (cur === themeId ? null : themeId));
  };

  const stopPlaying = () => {
    if (playRef.current) {
      clearInterval(playRef.current);
      playRef.current = null;
    }
    setPlaying(false);
  };

  const togglePlay = () => {
    if (playRef.current) {
      stopPlaying();
      return;
    }
    setWindowB(windowA);
    setPlaying(true);
    playRef.current = setInterval(() => {
      setWindowB((prevB) => {
        const next = prevB + 1;
        if (next >= lastIndex) {
          stopPlaying();
          return lastIndex;
        }
        return next;
      });
    }, PLAY_INTERVAL_MS);
  };

  const handleWindowAChange = (value: number) => {
    setWindowA(value);
    setWindowB((b) => (value > b ? value : b));
  };
  const handleWindowBChange = (value: number) => {
    setWindowB(value);
    setWindowA((a) => (value < a ? value : a));
  };

  const timeLabel =
    windowA === 0 && windowB === lastIndex
      ? t("stories.fullStory")
      : `${formatShortDate(sessions[windowA].date, i18n.language)} – ${formatShortDate(
          sessions[windowB].date,
          i18n.language,
        )}`;

  return (
    <div className="sty-map">
      <svg
        viewBox={`0 0 ${MAP_VIEW_W} ${MAP_VIEW_H}`}
        className="sty-map-svg"
      >
        <defs>
          <pattern
            id={gratId}
            width={26}
            height={26}
            patternUnits="userSpaceOnUse"
          >
            <circle cx={1} cy={1} r={0.9} fill="var(--color-brand)" opacity={0.1} />
          </pattern>
          <radialGradient id={vignId} cx="50%" cy="42%" r="80%">
            <stop offset="62%" stopColor="var(--color-surface)" stopOpacity={0} />
            <stop offset="100%" stopColor="var(--color-title)" stopOpacity={0.06} />
          </radialGradient>
        </defs>
        <rect
          className="sty-map-bg"
          width={MAP_VIEW_W}
          height={MAP_VIEW_H}
          rx={12}
        />
        <rect
          width={MAP_VIEW_W}
          height={MAP_VIEW_H}
          rx={12}
          fill={`url(#${gratId})`}
        />

        {/* Territories — declutter-by-default: only the focused theme's
            territory is drawn; at rest the map is a calm centroid constellation
            (8 overlapping ellipses were the chaos). */}
        {territoryThemes.map(({ theme, moments, e1, e16 }) => {
          if (focusedThemeId !== theme.id) return null;
          const tone = themeTone(theme);
          return (
            <g
              key={`territory-${theme.id}`}
              className="sty-map-territory"
              data-testid="territory"
              data-theme-id={theme.id}
              style={{ opacity: territoryOpacity(visibleFraction(moments)) }}
            >
              <ellipse
                cx={e1.cx}
                cy={e1.cy}
                rx={e1.rx}
                ry={e1.ry}
                transform={`rotate(${e1.angleDeg} ${e1.cx} ${e1.cy})`}
                fill={tone}
                fillOpacity={0.07}
                stroke={tone}
                strokeOpacity={0.35}
                strokeWidth={1.3}
              />
              <ellipse
                cx={e16.cx}
                cy={e16.cy}
                rx={e16.rx}
                ry={e16.ry}
                transform={`rotate(${e16.angleDeg} ${e16.cx} ${e16.cy})`}
                fill="none"
                stroke={tone}
                strokeOpacity={0.15}
                strokeWidth={1.1}
                strokeDasharray="1 4"
                strokeLinecap="round"
              />
            </g>
          );
        })}

        {/* Drift arrow for the focused theme only */}
        {focusedEntry && drift && (
          <g className="sty-map-drift" data-testid="drift">
            <circle
              cx={drift.startX}
              cy={drift.startY}
              r={3.5}
              fill="none"
              stroke={themeTone(focusedEntry.theme)}
              strokeWidth={1.6}
            />
            <line
              x1={drift.startX}
              y1={drift.startY}
              x2={drift.endX}
              y2={drift.endY}
              stroke={themeTone(focusedEntry.theme)}
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray="7 5"
            />
            <line
              x1={drift.endX}
              y1={drift.endY}
              x2={drift.arrow1[0]}
              y2={drift.arrow1[1]}
              stroke={themeTone(focusedEntry.theme)}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <line
              x1={drift.endX}
              y1={drift.endY}
              x2={drift.arrow2[0]}
              y2={drift.arrow2[1]}
              stroke={themeTone(focusedEntry.theme)}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <text
              x={drift.labelX}
              y={drift.labelY}
              textAnchor="middle"
              className="sty-map-drift-label"
              fill={themeTone(focusedEntry.theme)}
            >
              {t("stories.drift")}
            </text>
          </g>
        )}

        {/* Points — only the focused theme's moments are plotted; at rest the
            map shows just the constellation. */}
        {data.moments.map((m) => {
          if (m.theme_id !== focusedThemeId) return null; // null focus hides all
          const di = dateIndexOf(m);
          if (di > windowB) return null; // outside the time window: hidden
          const th = themeOf(m);
          const tone = themeTone(th);
          const { radius, fillOpacity } = inkAge(di, lastIndex);
          const isLatestSession = di === lastIndex;
          const isGhost = di < windowA;
          const opacity = isGhost ? 0.12 : fillOpacity;
          const [cx, cy] = projected.get(m.id)!;
          const dateLabel = formatShortDate(sessions[di].date, i18n.language);
          const label = `${themeLabel(th)}, ${dateLabel}`;
          return (
            <g
              key={`m-${m.id}`}
              className={"sty-map-pt" + (isGhost ? " sty-map-pt-ghost" : "")}
              data-moment-id={m.id}
              tabIndex={0}
              role="button"
              aria-label={label}
              onClick={() => onSelectMoment(m.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectMoment(m.id);
                }
              }}
              onPointerOver={(e) =>
                showTip({ themeLabel: themeLabel(th), tone, dateLabel, text: m.text }, e)
              }
              onPointerMove={moveTip}
              onPointerOut={hideTip}
            >
              {isLatestSession && (
                <circle
                  className="sty-map-halo"
                  cx={cx}
                  cy={cy}
                  r={radius + HALO_EXTRA}
                />
              )}
              {selectedMomentId === m.id && (
                <circle
                  data-testid="selection-ring"
                  className="sty-map-selring"
                  cx={cx}
                  cy={cy}
                  r={radius + SELECTION_EXTRA}
                />
              )}
              <circle
                className="sty-map-dot"
                cx={cx}
                cy={cy}
                r={radius}
                fill={tone}
                fillOpacity={opacity}
              />
            </g>
          );
        })}

        {/* Constellation — one labeled centroid per theme, always visible. This
            is the calm resting state; clicking a node focuses that theme
            (reveals its territory + dots + drift, dims the other nodes). */}
        {territoryThemes.map(({ theme, moments, e1 }) => {
          const tone = themeTone(theme);
          const isFocused = focusedThemeId === theme.id;
          const dimmed = focusedThemeId != null && !isFocused;
          const r = 4 + Math.sqrt(moments.length);
          return (
            <g
              key={`node-${theme.id}`}
              className={"sty-map-node" + (isFocused ? " sty-map-node-on" : "")}
              data-testid="territory-label"
              data-theme-id={theme.id}
              role="button"
              tabIndex={0}
              aria-pressed={isFocused}
              style={{ opacity: dimmed ? 0.4 : 1 }}
              onClick={() => toggleFocus(theme.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleFocus(theme.id);
                }
              }}
            >
              {/* Ghost ring — telegraphs that the node opens into a territory
                  (echoes the e16 dashed outline). Hidden once focused. */}
              <circle
                className="sty-map-nodering"
                cx={e1.cx}
                cy={e1.cy}
                r={r + 7}
                fill="none"
                stroke={tone}
              />
              <circle
                className="sty-map-nodedot"
                cx={e1.cx}
                cy={e1.cy}
                r={r}
                fill={tone}
              />
              <text
                x={e1.cx}
                y={e1.cy - r - 6}
                textAnchor="middle"
                className="sty-map-label"
                fill={tone}
              >
                {themeLabel(theme)}
              </text>
            </g>
          );
        })}

        <rect
          width={MAP_VIEW_W}
          height={MAP_VIEW_H}
          rx={12}
          fill={`url(#${vignId})`}
          pointerEvents="none"
        />
      </svg>

      <div className="sty-map-timebar">
        <button
          type="button"
          className="sty-map-play"
          data-testid="map-play"
          aria-label={t("stories.play")}
          onClick={togglePlay}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <div className="sty-map-dualslider">
          <div className="sty-map-track" />
          <div
            className="sty-map-fill"
            style={{
              left: `${(windowA / lastIndex) * 100}%`,
              width: `${((windowB - windowA) / lastIndex) * 100}%`,
            }}
          />
          <input
            type="range"
            min={0}
            max={lastIndex}
            value={windowA}
            aria-label={t("stories.windowStart")}
            data-testid="map-window-start"
            onChange={(e) => handleWindowAChange(Number(e.target.value))}
          />
          <input
            type="range"
            min={0}
            max={lastIndex}
            value={windowB}
            aria-label={t("stories.windowEnd")}
            data-testid="map-window-end"
            onChange={(e) => handleWindowBChange(Number(e.target.value))}
          />
        </div>
        <span className="sty-map-timelabel">{timeLabel}</span>
      </div>

      <div className="sty-map-legend">
        <i>{t("stories.legendContour")}</i>
        <i>{t("stories.legendOlder")}</i>
        <i>{t("stories.legendPre")}</i>
        <i className="sty-legend-latest">○ {t("stories.legendLatest")}</i>
      </div>

      {tooltip}
    </div>
  );
}
